import { useState } from 'react';
import type { QualificationType, PositionType } from '@keetcheck/shared';
import { theme } from '../../theme.js';
import { useAuth } from '../../context/AuthContext.js';
import { useUnitCatalogue, useSaveQualificationType, useSavePositionType } from '../../lib/queries.js';
import { Icon } from '../../components/Icon.js';

/**
 * The unit's qualification and position lists. Types are retired rather
 * than deleted, so members' history keeps making sense; retired ones are
 * hidden from pickers unless someone still holds them.
 */
export function QualificationsPage() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const { data: catalogue, isLoading } = useUnitCatalogue(orgId);
  const [addingQual, setAddingQual] = useState(false);
  const [addingPos, setAddingPos] = useState(false);

  return (
    <div style={{ flex: 1, background: theme.surface }}>
      <SectionHeader action={<button onClick={() => setAddingQual(!addingQual)} style={addBtn}><Icon name="plus" size={14} /> Add</button>}>
        Qualifications
      </SectionHeader>
      {addingQual && <QualForm orgId={orgId} onDone={() => setAddingQual(false)} />}
      {isLoading && <EmptyMsg>Loading…</EmptyMsg>}
      {catalogue?.qualifications.map((t) => <QualRow key={t.id} orgId={orgId} type={t} />)}

      <SectionHeader action={<button onClick={() => setAddingPos(!addingPos)} style={addBtn}><Icon name="plus" size={14} /> Add</button>}>
        Positions
      </SectionHeader>
      {addingPos && <PositionForm orgId={orgId} onDone={() => setAddingPos(false)} />}
      {catalogue?.positions.map((p) => <PositionRow key={p.id} orgId={orgId} position={p} />)}
      <div style={{ padding: '12px 18px 24px', fontSize: 12, color: theme.textMuted, lineHeight: 1.5 }}>
        Positions are labels only — they don't change what someone can do in the app.
        Set each member's qualifications and positions on the Members tab.
      </div>
    </div>
  );
}

function QualRow({ orgId, type }: { orgId: string; type: QualificationType }) {
  const saveMut = useSaveQualificationType(orgId);
  const [editing, setEditing] = useState(false);
  if (editing) return <QualForm orgId={orgId} type={type} onDone={() => setEditing(false)} />;
  return (
    <div style={{ ...row, opacity: type.active ? 1 : 0.55 }}>
      <strong style={{ width: 44, fontSize: 13, color: theme.text, flexShrink: 0 }}>{type.code}</strong>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, color: theme.text }}>{type.name}{!type.active && ' (retired)'}</div>
        {(type.hasExpiry || type.grades) && (
          <div style={{ fontSize: 12, color: theme.textMuted }}>
            {[type.hasExpiry && 'has expiry date', type.grades && `grades: ${type.grades.join(', ')}`].filter(Boolean).join(' · ')}
          </div>
        )}
      </div>
      <button aria-label="Edit qualification" onClick={() => setEditing(true)} title="Edit" style={iconBtnSmall}><Icon name="edit" size={12} /></button>
      <button onClick={() => saveMut.mutate({ id: type.id, active: !type.active })} disabled={saveMut.isPending} style={textBtn}>
        {type.active ? 'Retire' : 'Restore'}
      </button>
    </div>
  );
}

function QualForm({ orgId, type, onDone }: { orgId: string; type?: QualificationType; onDone: () => void }) {
  const saveMut = useSaveQualificationType(orgId);
  const [code, setCode] = useState(type?.code ?? '');
  const [name, setName] = useState(type?.name ?? '');
  const [hasExpiry, setHasExpiry] = useState(type?.hasExpiry ?? false);
  const [grades, setGrades] = useState(type?.grades?.join(', ') ?? '');

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const gradeList = grades.split(',').map((g) => g.trim()).filter(Boolean);
    saveMut.mutate(
      { id: type?.id, code: code.trim(), name: name.trim(), hasExpiry, grades: gradeList.length ? gradeList : null },
      { onSuccess: onDone },
    );
  }

  return (
    <form onSubmit={submit} style={formBox}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input required value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code" maxLength={10}
          aria-label="Code" style={{ ...input, width: 90, textTransform: 'uppercase' }} />
        <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" maxLength={100}
          aria-label="Name" style={{ ...input, flex: 1 }} />
      </div>
      <input value={grades} onChange={(e) => setGrades(e.target.value)} placeholder="Grades, comma separated (optional)"
        aria-label="Grades" style={input} />
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: theme.text }}>
        <input type="checkbox" checked={hasExpiry} onChange={(e) => setHasExpiry(e.target.checked)} />
        Has an expiry date
      </label>
      <div style={{ display: 'flex', gap: 10 }}>
        <button type="button" onClick={onDone} style={cancelBtn}>Cancel</button>
        <button type="submit" disabled={saveMut.isPending} style={submitBtn}>{saveMut.isPending ? 'Saving…' : 'Save'}</button>
      </div>
    </form>
  );
}

function PositionRow({ orgId, position }: { orgId: string; position: PositionType }) {
  const saveMut = useSavePositionType(orgId);
  const [editing, setEditing] = useState(false);
  if (editing) return <PositionForm orgId={orgId} position={position} onDone={() => setEditing(false)} />;
  return (
    <div style={{ ...row, opacity: position.active ? 1 : 0.55 }}>
      <div style={{ flex: 1, fontSize: 14, color: theme.text }}>{position.name}{!position.active && ' (retired)'}</div>
      <button aria-label="Rename position" onClick={() => setEditing(true)} title="Rename" style={iconBtnSmall}><Icon name="edit" size={12} /></button>
      <button onClick={() => saveMut.mutate({ id: position.id, active: !position.active })} disabled={saveMut.isPending} style={textBtn}>
        {position.active ? 'Retire' : 'Restore'}
      </button>
    </div>
  );
}

function PositionForm({ orgId, position, onDone }: { orgId: string; position?: PositionType; onDone: () => void }) {
  const saveMut = useSavePositionType(orgId);
  const [name, setName] = useState(position?.name ?? '');
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); saveMut.mutate({ id: position?.id, name: name.trim() }, { onSuccess: onDone }); }}
      style={{ ...formBox, flexDirection: 'row' }}
    >
      <input required autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Position name"
        maxLength={100} aria-label="Position name" style={{ ...input, flex: 1 }} />
      <button type="button" onClick={onDone} style={{ ...cancelBtn, flex: 'none', padding: '0 14px' }}>Cancel</button>
      <button type="submit" disabled={saveMut.isPending} style={{ ...submitBtn, flex: 'none', padding: '0 16px' }}>Save</button>
    </form>
  );
}

function SectionHeader({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '8px 16px 8px 18px', fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
      color: theme.textMuted, textTransform: 'uppercase',
      background: theme.bg, borderBottom: `1px solid ${theme.border}`,
    }}>
      {children}
      {action}
    </div>
  );
}

function EmptyMsg({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: 20, fontSize: 13, color: theme.textMuted, textAlign: 'center' }}>{children}</div>;
}

const row: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px',
  borderBottom: `1px solid ${theme.border}`,
};

const formBox: React.CSSProperties = {
  padding: '12px 16px', background: theme.surface2, borderBottom: `1px solid ${theme.border}`,
  display: 'flex', flexDirection: 'column', gap: 8,
};

const input: React.CSSProperties = {
  padding: '9px 12px', background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 10,
  fontSize: 13, color: theme.text, fontFamily: theme.font, outline: 'none', boxSizing: 'border-box', minWidth: 0,
};

const addBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 7,
  background: theme.accent, border: 'none', color: theme.accentInk, fontSize: 12, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font, textTransform: 'none', letterSpacing: 0,
};

const iconBtnSmall: React.CSSProperties = {
  width: 26, height: 26, borderRadius: 6, border: `1px solid ${theme.border}`, background: theme.surface,
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textMuted, flexShrink: 0,
};

const textBtn: React.CSSProperties = {
  padding: '4px 8px', borderRadius: 6, border: `1px solid ${theme.border}`, background: theme.surface,
  fontSize: 12, fontWeight: 600, color: theme.textDim, cursor: 'pointer', fontFamily: theme.font, flexShrink: 0,
};

const cancelBtn: React.CSSProperties = {
  flex: 1, padding: '9px 0', borderRadius: 10, background: theme.surface, border: `1px solid ${theme.border}`,
  color: theme.textDim, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font,
};

const submitBtn: React.CSSProperties = {
  flex: 2, padding: '9px 0', borderRadius: 10, background: theme.accent, border: 'none',
  color: theme.accentInk, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font,
};
